import React, { useMemo, useState } from 'react';
import { BookOpen, Check, Loader2, Plus } from 'lucide-react';
import type { Unterlage } from '../types';
import {
    katalogErklaerungZu, katalogVorschlaege, type DokumentErklaerung, type KatalogVorschlag,
} from '../ablage/katalogVorschlaege';

/**
 * Kapitel 9 aus dem Katalog mitgeltender Dokumente — die Anzeige.
 *
 * Welche Dokumente fehlen, wie die neue Zeile aussieht und woran eine
 * vorhandene Unterlage wiedererkannt wird, steht ohne DOM in
 * `../ablage/katalogVorschlaege.ts` und ist dort geprueft. Diese Datei zeigt
 * an und ruft auf.
 *
 * ═══ WARUM EIN UEBERNOMMENER EINTRAG ROT IST ══════════════════════════════
 * Der Katalog weiss, dass ein Dokument gebraucht wird — nicht, dass es
 * vorliegt. Die neue Zeile steht deshalb in Kapitel 9 als „nicht in der
 * Ablage", bis jemand ein Dokument verknuepft oder „wird nachgereicht"
 * setzt. Die Karte sagt das VOR dem Klick, damit niemand den roten Kasten
 * im Plan fuer einen Fehler haelt.
 */

const KNOPF = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold '
    + 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40';

/** Dieselben Worte wie die Auswahl „Art" in der Unterlagenliste. */
const ART_LABEL: Record<Unterlage['art'], string> = {
    grundlage: 'verwendete Unterlage',
    anlage: 'Anlage des Plans',
};

/**
 * Die aufklappbare Erklaerung eines Katalogdokuments.
 *
 * Zugeklappt, weil sie Hintergrund ist und keine Angabe des Plans: sie wird
 * nicht gedruckt, und eine Liste, in der jede Zeile fuenf Absaetze traegt,
 * liest niemand mehr.
 */
export const KatalogErklaerung: React.FC<{
    erklaerung: DokumentErklaerung;
    titel?: string;
}> = ({ erklaerung: e, titel = 'Erklärung aus dem Katalog' }) => {
    const zeilen = [
        { label: 'Wann benötigt', text: e.wannBenoetigt },
        { label: 'Für den SiGeKo relevant', text: e.sigekoRelevanz },
        { label: 'Vorlauf', text: e.vorlauf ?? '' },
        { label: 'Rechtsgrundlage', text: e.rechtsgrundlage.join('; ') },
        { label: 'Liefert', text: e.lieferanten.join(', ') },
    ].filter(z => z.text);
    if (zeilen.length === 0 && !e.praxis) return null;

    return (
        <details className="mt-1.5">
            <summary className="cursor-pointer select-none text-[11px] font-bold text-slate-500 hover:text-slate-700">
                {titel}
            </summary>
            <div className="mt-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                <dl className="space-y-1">
                    {zeilen.map(z => (
                        <div key={z.label} className="text-[11px]">
                            <dt className="font-bold text-slate-600">{z.label}</dt>
                            <dd className="text-slate-600">{z.text}</dd>
                        </div>
                    ))}
                </dl>
                {e.praxis && (
                    <p className="text-[11px] text-amber-700 mt-1">
                        Aus der Fachpraxis, nicht im Mängelkatalog belegt: vor der Freigabe prüfen.
                    </p>
                )}
            </div>
        </details>
    );
};

/**
 * Die Erklaerung zu einer vorhandenen Unterlage — nichts, wenn die
 * Bezeichnung keinem Katalogdokument entspricht (freier Eintrag).
 *
 * Der Titel nennt den Katalognamen: bei einer ergaenzten Bezeichnung
 * („Schadstoffgutachten, IGB vom 12.03.2025") sieht man so, worauf sich die
 * Erklaerung bezieht.
 */
export const KatalogErklaerungZuUnterlage: React.FC<{ bezeichnung: string }> = ({ bezeichnung }) => {
    const katalog = useMemo(() => katalogErklaerungZu(bezeichnung), [bezeichnung]);
    if (!katalog) return null;
    return (
        <KatalogErklaerung
            erklaerung={katalog.erklaerung}
            titel={`Erklärung aus dem Katalog: ${katalog.name}`}
        />
    );
};

interface Props {
    unterlagen: Unterlage[];
    /** Freigegeben oder Nur-Ansicht: es wird nichts übernommen. */
    gesperrt: boolean;
    /** Liefert die Zahl der angelegten Zeilen, `null` bei einem Fehler. */
    uebernehmen: (vorschlaege: readonly KatalogVorschlag[]) => Promise<number | null>;
}

export const MitgeltendeVorschlaege: React.FC<Props> = ({ unterlagen, gesperrt, uebernehmen }) => {
    const vorschlaege = useMemo(() => katalogVorschlaege(unterlagen), [unterlagen]);
    /** `'alle'` oder die Katalog-Id des Vorschlags, der gerade übernommen wird. */
    const [arbeitet, setArbeitet] = useState<string | null>(null);
    const [meldung, setMeldung] = useState('');

    const ausloesen = async (auswahl: KatalogVorschlag[], schluessel: string) => {
        if (arbeitet || auswahl.length === 0) return;
        setArbeitet(schluessel);
        setMeldung('');
        try {
            const angelegt = await uebernehmen(auswahl);
            if (!angelegt) return;
            setMeldung(angelegt === 1 && auswahl.length === 1
                ? `„${auswahl[0].bezeichnung}“ steht jetzt in Kapitel 9, rot als „nicht in der Ablage“, `
                    + 'bis ein Dokument verknüpft oder „wird nachgereicht“ gesetzt ist.'
                : `${angelegt} Unterlagen stehen jetzt in Kapitel 9, rot als „nicht in der Ablage“, `
                    + 'bis ein Dokument verknüpft oder „wird nachgereicht“ gesetzt ist.');
        } finally {
            setArbeitet(null);
        }
    };

    // Nichts vorzuschlagen (oder der Katalog noch leer): die Karte bleibt
    // ruhig. Nur die Rueckmeldung der letzten Uebernahme bleibt stehen.
    if (vorschlaege.length === 0 && !meldung) return null;

    return (
        <div className="mt-3 rounded-xl border border-dashed border-slate-300 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                <p className="text-sm font-bold text-slate-700 inline-flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4 text-slate-400 shrink-0" />
                    Vorschläge aus dem Katalog mitgeltender Dokumente
                </p>
                {!gesperrt && vorschlaege.length > 1 && (
                    <button
                        type="button"
                        className={KNOPF}
                        disabled={arbeitet !== null}
                        onClick={() => void ausloesen(vorschlaege, 'alle')}
                    >
                        {arbeitet === 'alle'
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Plus className="w-3.5 h-3.5" />}
                        Alle übernehmen ({vorschlaege.length})
                    </button>
                )}
            </div>
            <p className="text-[11px] text-slate-500 mb-2">
                {gesperrt
                    ? 'Der Plan ist freigegeben oder nur zur Ansicht geöffnet, hier wird nichts übernommen.'
                    : 'Übernommen steht ein Eintrag in Kapitel 9 als „nicht in der Ablage“ (rot), bis ein '
                        + 'Dokument der Projektablage verknüpft oder „wird nachgereicht“ gesetzt ist. '
                        + 'Als Herausgeber steht zunächst die Rolle; bitte durch den tatsächlichen ersetzen.'}
            </p>

            {meldung && (
                <p className="mb-2 text-xs text-emerald-700 flex items-start gap-1">
                    <Check className="w-3.5 h-3.5 shrink-0 mt-px" /> {meldung}
                </p>
            )}

            {vorschlaege.length > 0 && (
                <ul className="space-y-1.5">
                    {vorschlaege.map(v => (
                        <li key={v.dokumentId} className="rounded-lg bg-slate-50 px-2.5 py-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-slate-700">{v.bezeichnung}</p>
                                    <p className="text-[11px] text-slate-500">
                                        {ART_LABEL[v.art]}
                                        {v.herausgeber && ` · ${v.herausgeber}`}
                                    </p>
                                </div>
                                {!gesperrt && (
                                    <button
                                        type="button"
                                        className={`${KNOPF} shrink-0`}
                                        disabled={arbeitet !== null}
                                        onClick={() => void ausloesen([v], v.dokumentId)}
                                    >
                                        {arbeitet === v.dokumentId
                                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            : <Plus className="w-3.5 h-3.5" />}
                                        übernehmen
                                    </button>
                                )}
                            </div>
                            <KatalogErklaerung erklaerung={v.erklaerung} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
